import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import copy_key_img from "./images/copy_key.png";
import create_bot_user_img from "./images/create_bot_user.png";
import generate_token_modal_img from "./images/generate_token_modal.png";
import invite_people_img from "./images/invite_people.png";
import my_settings_img from "./images/my_settings_navigate.png";
import new_api_token_img from "./images/new_api_token.png";
import people_management_img from "./images/people_management_navigate.png";

const SecurityScorecard = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const queryClient = useQueryClient();
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated("securityscorecard");
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);

  const formInitialValues = {
    api_token: "",
  };

  const formCheckoutSchema = yup.object().shape({
    api_token: yup.string().required("required"),
  });

  const { registerSecurityScorecardIntegration } = useAPI();
  const handleFormSubmit = (values) => {
    setLoading(true);
    registerSecurityScorecardIntegration(values.api_token.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("SecurityScorecard integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Security Scorecard and prioritize the suggested remediation."
            : ""
        }
        image={logos.SecurityScorecard}
        iconBorderRadius={0}
        name={"Security Scorecard"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Security Scorecard is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <GuidelineStepByStep>
                  <GuidelineStep title="Log in to SecurityScorecard">
                    <TextWithExternalLink
                      href="https://platform.securityscorecard.io"
                      text="Log in to SecurityScorecard using an admin account"
                      linkText={"SecurityScorecard"}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Set Up a Bot User">
                    <Typography variant="body2">
                      Click on the profile icon, then choose
                      <strong>"My Settings"</strong> from the dropdown menu.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: my_settings_img,
                          altext: "My Settings",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Go to the <strong>"People Management"</strong> section.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: people_management_img,
                          altext: "People Management",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Click on the <strong>"Invite People"</strong> button.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: invite_people_img,
                          altext: "Invite People",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Check the box labeled
                      <strong>"Check to create a bot user"</strong> to create a
                      bot user.
                    </Typography>
                    <Typography variant="body2">
                      Enter a<strong> Bot Name</strong> for example
                      "Breeze-Bot".
                    </Typography>
                    <Typography variant="body2">
                      Set the <strong>Access Level</strong> - "Read Only".
                    </Typography>
                    <Typography variant="body2">
                      Click <strong>"Add User"</strong> to save.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: create_bot_user_img,
                          altext: "Create a Bot User",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Generate an API Token for the Bot">
                    <Typography variant="body2">
                      In the <strong>"People Management"</strong> section, hover
                      over the new bot user's API cell and click on the{" "}
                      <strong>"New API Token"</strong> button.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: new_api_token_img,
                          altext: "Generate a New API Token",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                    <Typography variant="body2">
                      Click <strong>"Confirm"</strong> to generate the token.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: generate_token_modal_img,
                          altext: "Generate a New API Token",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={formInitialValues}
                    validationSchema={formCheckoutSchema}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                    }) => (
                      <Form>
                        <GuidelineStep title="Copy Your API Token">
                          <Typography variant="body2">
                            A new window will open and display the generated
                            token.
                          </Typography>
                          <Typography variant="body2">
                            <strong>Copy the token</strong>. You will not be
                            able to view it again after you close the window.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: copy_key_img,
                                altext: "Copy the Token",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant="body2">
                            Paste the token below.
                          </Typography>

                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="api_token"
                              label="API Token"
                              value={values.api_token}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              type={showSecret ? "text" : "password"}
                              error={!!touched.api_token && !!errors.api_token}
                              helperText={touched.api_token && errors.api_token}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle token visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                          </Box>
                          <PrimaryButton
                            sx={{ width: "100px" }}
                            type="submit"
                            disabled={guideMode}
                          >
                            Connect
                          </PrimaryButton>
                        </GuidelineStep>
                      </Form>
                    )}
                  </Formik>
                </GuidelineStepByStep>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};
export default SecurityScorecard;
